import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Users, Plus, Copy, CheckCircle2,
  Clock, HandHeart, UserPlus, Handshake, Sparkles,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const STATUS = {
  completed: { label: "Completed", icon: CheckCircle2, cls: "text-emerald-600" },
  in_progress: { label: "In progress", icon: Clock, cls: "text-gold-600" },
  needs_encouragement: { label: "Needs encouragement", icon: HandHeart, cls: "text-terracotta-500" },
};
const REACTIONS = [
  ["mashallah", "MashaAllah"], ["barik", "Allahumma Barik"], ["subhanallah", "SubhanAllah"], ["dua", "Make Dua 🤲"],
];
const DUAS = ["MashaAllah", "Keep going", "May Allah make it easy", "We're with you"];

export default function Community() {
  const { tx, t: tr } = useLanguage();
  const [feed, setFeed] = useState([]);
  const [circles, setCircles] = useState([]);
  const [buddy, setBuddy] = useState(null);
  const [openCircle, setOpenCircle] = useState(null);
  const [form, setForm] = useState(null); // {name, type} | {code}
  const load = () => {
    api.get("/feed").then((r) => setFeed(r.data)).catch(() => {});
    api.get("/circles").then((r) => setCircles(r.data)).catch(() => {});
    api.get("/buddy").then((r) => setBuddy(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const react = async (ev, reaction) => {
    try {
      const r = await api.post(`/feed/${ev.event_id}/react`, { reaction });
      setFeed((f) => f.map((x) => (x.event_id === ev.event_id ? r.data : x)));
    } catch { /* noop */ }
  };

  const openDetail = async (c) => {
    const r = await api.get(`/circles/${c.circle_id}`);
    setOpenCircle(r.data);
  };

  const encourage = async (circleId, toUserId, message) => {
    await api.post(`/circles/${circleId}/encourage`, { to_user_id: toUserId, message });
    toast.success(tr("community.sent", { message }));
    load();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="community-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{tr("community.community")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{tr("community.youNeverMemorizeAlone")}</h1>
      </header>

      {/* Buddy */}
      <section className="hifz-card" data-testid="buddy-card">
        <h2 className="font-display font-semibold text-xl text-forest-700 inline-flex items-center gap-2">
          <Handshake className="h-5 w-5" /> {tr("community.myQuranCompanion")}
        </h2>
        {buddy?.buddy ? (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-semibold text-forest-700">{buddy.buddy.name}</div>
              <div className="text-xs text-muted-foreground">
                {buddy.buddy.needs_encouragement
                  ? tr("community.hasnTStudiedForDays", { days_quiet: buddy.buddy.days_quiet })
                  : tr("community.dayStreakWalkingTheJourney", { streak: buddy.buddy.streak })}
              </div>
              {buddy.buddy.risk_alert && (
                <div className="mt-1 text-xs font-medium text-terracotta-500" data-testid="buddy-risk-alert">
                  {tr("community.buddyMayBeStruggling", { name: buddy.buddy.name?.split(" ")[0] })}
                </div>
              )}
            </div>
            <div className="flex gap-1.5 flex-wrap items-center">
              <button className="text-xs text-muted-foreground underline mr-2" data-testid="buddy-leave"
                onClick={async () => { try { await api.post("/buddy/leave"); toast.success(tr("community.companionshipEnded")); } catch (e) { /* ignore */ } load(); }}>
                {tr("community.endCompanionship")}
              </button>
              {DUAS.map((d) => (
                <button key={d} onClick={() => encourage("buddy", buddy.buddy.user_id, d)}
                  className="btn-ghost text-xs px-2.5 py-1.5" data-testid={`buddy-dua-${d.split(" ")[0].toLowerCase()}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            {(buddy?.incoming_requests || []).map((rq) => (
              <div key={rq.request_id} className="basis-full rounded-xl border border-gold-500/40 bg-gold-500/5 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="buddy-incoming-request">
                <span className="text-sm text-forest-700 font-medium">{tr("community.wantsToBeYourCompanion", { name: rq.from_name })}</span>
                <span className="flex gap-2">
                  <button className="btn-primary text-xs" data-testid="buddy-accept"
                    onClick={async () => { try { await api.post(`/buddy/requests/${rq.request_id}/accept`); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } load(); }}>
                    {tr("community.accept")}
                  </button>
                  <button className="btn-ghost text-xs" data-testid="buddy-decline"
                    onClick={async () => { try { await api.post(`/buddy/requests/${rq.request_id}/decline`); } catch (e) { /* already answered */ } load(); }}>
                    {tr("community.decline")}
                  </button>
                </span>
              </div>
            ))}
            {(buddy?.outgoing_requests || []).map((rq) => (
              <div key={rq.request_id} className="basis-full flex items-center justify-between gap-3 flex-wrap text-sm text-muted-foreground" data-testid="buddy-outgoing-request">
                <span>{tr("community.waitingForReply", { name: rq.to_name })}</span>
                <button className="btn-ghost text-xs"
                  onClick={async () => { try { await api.post(`/buddy/requests/${rq.request_id}/cancel`); } catch (e) { /* already answered */ } load(); }}>
                  {tr("community.cancelRequest")}
                </button>
              </div>
            ))}
            <p className="text-sm text-muted-foreground">
              {tr("community.pickOneCompanionToWalk")} <strong className="text-forest-700">{buddy?.code}</strong>
            </p>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(buddy?.code || ""); toast.success(tr("community.codeCopied")); }}
                className="btn-ghost text-xs" data-testid="buddy-copy-code"><Copy className="h-3.5 w-3.5" /> {tr("community.copyCode")}</button>
              <button onClick={() => setForm({ mode: "buddy", code: "" })} className="btn-accent text-xs" data-testid="buddy-join-btn">
                <UserPlus className="h-3.5 w-3.5" /> {tr("community.enterAFriendSCode")}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Circles */}
      <section data-testid="circles-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-semibold text-2xl text-forest-700">{tr("community.myCircles")}</h2>
          <div className="flex gap-2">
            <button onClick={() => setForm({ mode: "join", code: "" })} className="btn-ghost text-xs" data-testid="circle-join-btn">{tr("community.joinWithCode")}</button>
            <button onClick={() => setForm({ mode: "create", name: "", type: "family" })} className="btn-primary text-xs" data-testid="circle-create-btn">
              <Plus className="h-3.5 w-3.5" /> {tr("community.newCircle")}
            </button>
          </div>
        </div>
        {circles.length === 0 ? (
          <div className="hifz-card border-dashed text-center p-8" data-testid="circles-empty">
            <Users className="h-7 w-7 mx-auto text-gold-600" />
            <p className="mt-2 text-sm text-foreground/70">{tr("community.smallCirclesFamilyFriendsHalaqa")}</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {circles.map((c) => (
              <button key={c.circle_id} onClick={() => openDetail(c)} className="hifz-card hifz-card-hover text-left" data-testid={`circle-${c.circle_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-forest-700">{c.name}</div>
                  <span className="status-pill status-revising text-[10px] capitalize">{c.type}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">{c.member_count}/8 members · code <strong>{c.code}</strong></div>
              </button>
            ))}
          </div>
        )}
        {openCircle && (
          <div className="hifz-card mt-4" data-testid="circle-detail">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg text-forest-700">{tr("community.today", { name: openCircle.name })}</h3>
              <button onClick={() => setOpenCircle(null)} className="text-xs text-muted-foreground hover:underline">{tr("community.close")}</button>
            </div>
            <div className="mt-3 divide-y divide-sand-200">
              {openCircle.members.map((m) => {
                const S = STATUS[m.status];
                return (
                  <div key={m.user_id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`member-${m.user_id}`}>
                    <div className="flex items-center gap-2.5">
                      <S.icon className={`h-4.5 w-4.5 h-5 w-5 ${S.cls}`} />
                      <span className="text-sm font-medium">{m.name}{m.is_me ? " (you)" : ""}</span>
                      <span className={`text-xs ${S.cls}`}>{tx(S.label)}</span>
                    </div>
                    {!m.is_me && m.status === "needs_encouragement" && (
                      <div className="flex gap-1.5 flex-wrap">
                        {DUAS.slice(0, 3).map((d) => (
                          <button key={d} onClick={() => encourage(openCircle.circle_id, m.user_id, d)}
                            className="btn-ghost text-[11px] px-2 py-1" data-testid={`encourage-${m.user_id}-${d.split(" ")[0].toLowerCase()}`}>
                            {d} 🤲
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Feed */}
      <section data-testid="community-feed">
        <h2 className="font-display font-semibold text-2xl text-forest-700 mb-4 inline-flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-gold-600" /> {tr("community.theUmmahToday")}
        </h2>
        {feed.length === 0 ? (
          <div className="hifz-card border-dashed text-center p-8 text-sm text-muted-foreground">{tr("community.theFirstInspiringMomentWill")}</div>
        ) : (
          <div className="space-y-3">
            {feed.map((ev) => (
              <div key={ev.event_id} className="hifz-card py-4" data-testid={`feed-${ev.event_id}`}>
                <p className="text-foreground/85">{tx(ev.text)}</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {REACTIONS.map(([k, label]) => {
                    const count = (ev.reactions?.[k] || []).length;
                    return (
                      <button key={k} onClick={() => react(ev, k)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                          count ? "bg-gold-500/12 border-gold-500/40 text-gold-700" : "border-sand-300 hover:bg-sand-200"
                        }`} data-testid={`react-${ev.event_id}-${k}`}>
                        {label}{count ? ` · ${count}` : ""}
                      </button>
                    );
                  })}
                  <span className="ml-auto text-[10px] text-muted-foreground">{new Date(ev.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Create/Join dialog */}
      {form && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setForm(null)} data-testid="circle-dialog">
          <div className="bg-sand-100 rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-semibold text-xl text-forest-700">
              {form.mode === "create" ? tr("community.newCircle") : form.mode === "join" ? tr("community.joinACircle") : tr("community.becomeCompanions")}
            </h3>
            {form.mode === "create" ? (
              <>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={tr("community.circleNameEGThe")}
                  className="mt-4 w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="circle-name-input" />
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="circle-type-select">
                  {["family", "friends", "halaqa", "masjid"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={async () => {
                  const r = await api.post("/circles", { name: form.name, type: form.type });
                  toast.success(tr("community.circleCreatedInviteCode", { code: r.data.code }));
                  setForm(null); load();
                }} disabled={!form.name?.trim()} className="btn-primary w-full justify-center mt-4 disabled:opacity-50" data-testid="circle-create-submit">
                  {tr("community.create")}
                </button>
              </>
            ) : (
              <>
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder={tr("community.enterInviteCode")}
                  className="mt-4 w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="join-code-input" />
                <button onClick={async () => {
                  try {
                    if (form.mode === "buddy") {
                      const r = await api.post("/buddy/join", { code: form.code });
                      toast.success(r.data.status === "paired"
                        ? tr("community.youAndAreNowQuran", { buddy_name: r.data.buddy_name })
                        : tr("community.requestSentTo", { buddy_name: r.data.buddy_name }));
                    } else {
                      const r = await api.post("/circles/join", { code: form.code });
                      toast.success(tr("community.joinedWelcome", { name: r.data.name }));
                    }
                    setForm(null); load();
                  } catch (e) { toast.error(e?.response?.data?.detail || tr("halaqa.couldnTJoin")); }
                }} disabled={!form.code?.trim()} className="btn-primary w-full justify-center mt-4 disabled:opacity-50" data-testid="join-submit">
                  {tr("community.join")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
